import React from 'react'
import { Page, Link as LinkPDF, Text as TextPDF, Image, View, Font, Document, StyleSheet } from '@react-pdf/renderer'

import { getRecommendationIcon } from '../components'
import { renderAnswerValue, getRecommendation } from '../utils'

Font.register({
  family: 'RobotoSlab',
  fonts: [
    { src: `${process.env.PUBLIC_URL}/RobotoSlab-Regular.ttf` },
    { src: `${process.env.PUBLIC_URL}/RobotoSlab-Bold.ttf`, fontWeight: 'bold' },
    { src: `${process.env.PUBLIC_URL}/RobotoSlab-Light.ttf`, fontWeight: 'light' }
  ]
})

Font.registerHyphenationCallback(word => [word])

const styles = StyleSheet.create({
  page: {
    padding: 35,
    fontFamily: 'RobotoSlab'
  },
  title: {
    fontSize: 24,
    textAlign: 'center'
  },
  box: {
    flexDirection: 'column'
  },
  flex: {
    flexDirection: 'row'
  },
  text: {
    marginBottom: 3
  },
  heading: {
    marginBottom: 8
  },
  border: {
    borderWidth: 1,
    borderColor: 'red'
  },

  card: {
    padding: 10,
    borderWidth: 1,
    borderColor: 'grey',
    marginBottom: 10
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center'
  }
})

const fontSizes = {
  sm: {
    fontSize: 8
  },
  md: {
    fontSize: 10
  },
  xl: {
    fontSize: 13
  },
  xxl: {
    fontSize: 16
  },
  xxxl: {
    fontSize: 22
  },
  xxxxl: {
    fontSize: 24
  }
}

const combine = (...args) => args.flat()

const Text = ({ size = 'md', weight = 'normal', align = 'left', style, ...props }) => (
  <TextPDF
    {...props}
    style={combine(styles.text, fontSizes[size], { fontWeight: weight, textAlign: align }, style)}
  />
)

const Link = ({ size = 'md', weight = 'normal', align = 'left', style, ...props }) => (
  <LinkPDF
    {...props}
    style={combine(styles.text, fontSizes[size], { fontWeight: weight, textAlign: align }, style)}
  />
)

const Heading = ({ size = 'xl', weight = 'bold', style, ...props }) => (
  <Text {...props} size={size} weight={weight} style={combine(styles.heading, style)} />
)

const Box = ({ style, ...props }) => (
  <View {...props} style={combine({ flexDirection: 'column' }, style)} />
)
const Flex = ({ style, ...props }) => (
  <View {...props} style={combine({ display: 'flex', flexDirection: 'row' }, style)} />
)

export const FormSubmissionReportPDF = ({ auth, data }) => {
  const answers = data?.dimensionScores
    .slice()
    .sort((a, b) => a.dimension.scale - b.dimension.scale)
    .map(({ dimension, score }, index) => ({
      score,
      dimension,
      answers: data?.answers.filter(answer => answer.question.dimension.id === dimension.id)
    }))

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <Box fixed style={{ marginBottom: 10 }}>
          <Heading size='xxl'>ia.tic.gov.pt</Heading>
        </Box>
        <Heading size='xxxxl' align='center' style={{ marginTop: 10, marginBottom: 30 }}>
          Relatório de Avaliação #{data.id}
        </Heading>
        <Flex>
          <Box style={[styles.card, { flex: 3, marginRight: 10 }]}>
            <Heading size='xl' align='center'>
              Dados da Submissão
            </Heading>
            <Flex>
              <Text weight='bold' style={{ width: 65 }}>
                Data:
              </Text>
              <Text style={{ flex: 1 }}>16/02/2021, 16:18:57</Text>
            </Flex>
            <Flex>
              <Text weight='bold' style={{ width: 65 }}>
                Nome:
              </Text>
              <Text style={{ flex: 1 }}>{data?.form.name}</Text>
            </Flex>
            <Flex>
              <Text weight='bold' style={{ width: 65 }}>
                Utilizador:
              </Text>
              <Text style={{ flex: 1 }}>{auth?.name}</Text>
            </Flex>
          </Box>

          <Box style={[styles.card, { flex: 4, marginRight: 10 }]}>
            <Heading size='xl' align='center'>
              Identificação do Projeto
            </Heading>
            <Flex>
              <Text weight='bold' style={{ width: 65 }}>
                Projeto:
              </Text>
              <Text style={{ flex: 1 }}>{data?.user.entity.projects[0].name}</Text>
            </Flex>
            <Flex>
              <Text weight='bold' style={{ width: 65 }}>
                Maturidade:
              </Text>
              <Text style={{ flex: 1 }}>{data?.user.entity.projects[0].stage.name}</Text>
            </Flex>
            <Flex>
              <Text weight='bold' style={{ width: 65 }}>
                Setores:
              </Text>
              <Text style={{ flex: 1 }}>
                {data?.user.entity.projects[0].sectors.map(e => e.name).join(', ')}
              </Text>
            </Flex>
          </Box>

          <Box style={[styles.card, { flex: 1 }]}>
            <Heading size='xl' align='center'>
              Score
            </Heading>
            <Flex style={{ flex: 1, alignItems: 'center' }}>
              <Text size='xxxxl' align='center'>
                {(data?.score * 100)?.toFixed(0)}
              </Text>
            </Flex>
          </Box>
        </Flex>
        <Heading size='xxl' align='center' style={{ marginTop: 20, marginBottom: 20 }}>
          Recomendaçōes da Avaliação
        </Heading>
        {answers?.map(({ dimension: { id, name }, score, answers }, index) => (
          <Box key={index}>
            <Flex style={{ marginTop: 5, marginBottom: 5 }}>
              <Heading style={{ flex: 1 }}>{name}</Heading>
              <Heading>Score: </Heading>
              <Heading align='center' style={{ width: 20 }}>
                {(score * 100).toFixed(0)}
              </Heading>
            </Flex>
            {answers.map((answer, index) => {
              const recommendation = getRecommendation(answer)
              return (
                <Flex
                  key={index}
                  wrap={false}
                  style={{
                    border: 1,
                    borderColor: 'grey',
                    alignItems: 'center',
                    padding: 10,
                    marginBottom: 10
                  }}>
                  <Box style={{ flex: 1 }}>
                    <Flex>
                      <Text weight='bold' style={{ width: 80 }}>
                        Pergunta:
                      </Text>
                      <Text style={{ flex: 1 }}>{answer.question.text}</Text>
                    </Flex>

                    <Flex>
                      <Text size='sm' style={{ width: 80 }}>
                        Resposta:
                      </Text>
                      <Text size='sm' style={{ flex: 1 }}>
                        {renderAnswerValue(answer)}
                      </Text>
                    </Flex>
                    <Flex style={{ marginTop: 10 }}>
                      <Text weight='bold' style={{ width: 80 }}>
                        Recomendação:
                      </Text>
                      <Text style={{ flex: 1 }}>{recommendation.text}</Text>
                    </Flex>
                    {answer?.question?.associatedLink && (
                      <Flex>
                        <Text size='sm' style={{ width: 80 }}>
                          Sugestão de leitura:
                        </Text>
                        <Link size='sm' style={{ flex: 1 }} src={answer?.question?.associatedLink}>
                          Link
                        </Link>
                      </Flex>
                    )}
                  </Box>
                  {recommendation.score !== null && (
                    <Image
                      style={{ height: 30, width: 30, marginLeft: 10 }}
                      src={`${getRecommendationIcon(recommendation.score)}`}
                    />
                  )}
                </Flex>
              )
            })}
          </Box>
        ))}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  )
}

export default FormSubmissionReportPDF
